import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

/* ===========================================================================
 * ONE ANSWER TO "HAS THE PAGE SCROLLED?", SHARED BY EVERY BAR THAT CARES.
 *
 * Two elements in this app change appearance once the content has scrolled 12px
 * under them: the shell's 56px top bar, and the page-level sticky toolbar that
 * `PageShell` draws under it. They must flip on the SAME pixel — a toolbar that
 * gains its shadow at a different moment than the bar above it reads as two
 * independent pieces of chrome rather than one stack.
 *
 * They also must not each pay for the answer. The handoff's `attachStickyBar`
 * adds a passive `scroll` listener per bar and compares `scrollTop > 12` on
 * every event; with a bar in the shell and a bar on each of ~50 screens that is
 * a listener per page re-deriving a boolean that flipped in the first 12 pixels
 * of a 900px page. So the threshold is measured ONCE, by geometry:
 *
 *   · a 12px sentinel is pinned to the top of the scrolled content (AppShell);
 *   · an IntersectionObserver reports when it leaves the scrollport, which is
 *     `scrollTop > 12` expressed as a shape rather than as arithmetic, and the
 *     browser evaluates it off the main thread and calls back only when the
 *     ANSWER changes;
 *   · the boolean is published on this context, so a page-level bar subscribes
 *     instead of observing.
 *
 * WHY A CONTEXT AND NOT A STORE. The state already lives in `AppShell`, whose
 * subtree contains the `<Outlet />` — so a re-render is happening regardless of
 * how the value travels, and a store would add a second source of truth for a
 * fact the shell already owns. A context is the value it already has, handed
 * down.
 *
 * DEFAULT `false`, AND THAT IS THE CORRECT DEGRADATION. A `PageShell` rendered
 * outside the app (a test, a storybook page) or in a runtime with no
 * IntersectionObserver (jsdom) simply never enters the scrolled state. The state
 * is a shadow over a bar that is already sticky, opaque and bordered without it.
 * ======================================================================== */

const PageScrolledContext = createContext(false);

/**
 * Has the shell's content column been scrolled past the 12px threshold?
 *
 * Read by `PageShell`'s sticky toolbar band. Never by a screen directly — a
 * screen that wants a scrolled-state treatment wants the toolbar band, and if
 * it genuinely wants something else it should say so here rather than grow a
 * second threshold.
 */
export function usePageScrolled(): boolean {
  return useContext(PageScrolledContext);
}

/** Publishes the shell's scrolled state to the routed page. Rendered by `AppShell`. */
export function PageScrolledProvider({
  value,
  children,
}: {
  value: boolean;
  children: ReactNode;
}): JSX.Element {
  return <PageScrolledContext.Provider value={value}>{children}</PageScrolledContext.Provider>;
}

/**
 * The measurement itself: true once `sentinelRef` has left `rootRef`'s
 * scrollport. The sentinel's own HEIGHT is the threshold — see the note on the
 * 12px tripwire in `AppShell` — so this hook takes no pixel number and cannot
 * disagree with the element it observes.
 *
 * `enabled` exists because the shell swaps itself for the PIN pad while the
 * device lock is up, at which point neither ref is attached. Without it the
 * effect would run once against two nulls and never run again, and the bar
 * would silently never gain its shadow for the rest of a locked-then-unlocked
 * session.
 */
export function useScrolledPastSentinel(
  rootRef: RefObject<Element | null>,
  sentinelRef: RefObject<Element | null>,
  enabled = true
): boolean {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setScrolled(false);
      return;
    }
    const root = rootRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;
    // Feature-detected rather than assumed: jsdom implements no
    // IntersectionObserver at all, and the shell's own tests mount the
    // component that calls this.
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry) setScrolled(!entry.isIntersecting);
      },
      { root }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // The refs are stable containers; `enabled` is the only thing that can
    // change which elements exist to observe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return scrolled;
}
